import { usePostFrontFile } from '@framework/api-client/api/inquiries-compile/file-controller/file-controller.ts';
import { RichTextAdvancedInput } from '@framework/engine/inputs/rich-text-advanced/RichTextAdvancedInput.tsx';
import { RichTextAdvancedSchema } from '@framework/engine/inputs/rich-text-advanced/schema/RichTextAdvancedSchema.ts';
import { type IWidgetComponentProps } from '@framework/engine/schema/widgets/IWidgetType.ts';
import { IconType } from '@framework/engine/schema/widgets/repository/advanced/buttons/icons/Icons.tsx';
import {
  ButtonAlign,
  ButtonType,
} from '@framework/engine/schema/widgets/repository/advanced/buttons/types/ButtonEnums.ts';
import { AlignmentSelectInput } from '@framework/engine/schema/widgets/repository/common/AlignmentSelectInput.tsx';
import { BaseLayoutColSpanInput } from '@framework/engine/schema/widgets/repository/common/BaseLayoutColSpanInput.tsx';
import { ButtonTypeSelectInput } from '@framework/engine/schema/widgets/repository/common/ButtonTypeSelectInput.tsx';
import { IconTypeSelectInput } from '@framework/engine/schema/widgets/repository/common/IconTypeSelectInput.tsx';
import { CommonBaseLayoutSchema } from '@framework/engine/schema/widgets/repository/graphics/common/CommonBaseLayoutSchema.tsx';
import { CommonGraphicWidgetConfigSchema } from '@framework/engine/schema/widgets/repository/graphics/common/CommonGraphicWidgetConfigSchema.ts';
import { getWidgetProps } from '@framework/engine/schema/widgets/repository/inputs/common/util/getWidgetProps.ts';
import { useField } from '@framework/form/field/useField.tsx';
import { MenuPresets } from '@framework/form/inputs/rich-text/menu/MenuPresets.tsx';
import { UploadInput } from '@framework/form/inputs/upload/UploadInput.tsx';
import { FormItem } from '@framework/form/item/FormItem.tsx';
import { RowElement } from '@framework/ui/components/row/RowElement.tsx';
import { yup } from '@framework/validation/initYup.ts';
import { message, type UploadFile } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { type IDownloadWidget, WidgetDownloadType } from './IDownloadWidget.ts';

export const DownloadWidgetConfigSchema: yup.ObjectSchema<IDownloadWidget> =
  CommonGraphicWidgetConfigSchema.shape({
    type: yup.string().exact(WidgetDownloadType).required(),
    label: RichTextAdvancedSchema.optional().label('Etichetta'),

    layout: CommonBaseLayoutSchema,
    filePath: yup.string().label('File').required(),
  });

export interface IDownloadWidgetConfigComponentProps
  extends IWidgetComponentProps<IDownloadWidget> {}

/**
 * Configurazione del widget Download
 */
export function DownloadWidgetConfigComponent(
  props: IDownloadWidgetConfigComponentProps,
) {
  const options = getWidgetProps(props);
  const uploadMutation = usePostFrontFile();
  const [fileList, setFileList] = useState<UploadFile[]>();
  const filePathField = useField('filePath');

  const handleCustomRequest = useCallback(
    (options: any) => {
      const { file, onError } = options;
      if (!file) {
        console.error('[Upload] Error: file not found');
        return;
      }

      uploadMutation
        .mutateAsync({
          data: { file },
        })
        .then(filePath => {
          filePathField.setValue(filePath);
          setFileList([file]);
          message.success('File caricato con successo');
        })
        .catch(err => {
          console.error('[Upload] Error after API', err);
          onError?.(err);
          // biome-ignore format: <tbd>
          message.error( err.response?.data?.descrizione ?? 'Errore durante il caricamento del file');
        });
    },
    [filePathField, uploadMutation],
  );

  const handleRemove = useCallback(() => {
    setFileList([]);
    filePathField.setValue('');
  }, [filePathField]);

  // Carica il file selezionato
  useEffect(() => {
    if (props.widget.filePath) {
      const [_, fileName] = props.widget.filePath.split('/');
      const file: UploadFile = {
        uid: '-1',
        name: fileName,
        status: 'done',
      };

      setFileList([file]);
    }
  }, [props.widget.filePath]);

  return (
    <div>
      <BaseLayoutColSpanInput isTableColumn={options.isTableColumn} />
      <RowElement>
        <FormItem name="layout.buttonType" label="Tipo di pulsante">
          <ButtonTypeSelectInput
            name="layout.buttonType"
            defaultValue={ButtonType.Primary}
            placeholder="Seleziona il tipo di pulsante"
          />
        </FormItem>
        <FormItem name="layout.iconType" label="Icona del pulsante">
          <IconTypeSelectInput
            name="layout.iconType"
            defaultValue={IconType.Nessuna}
            placeholder="Seleziona l'icona del pulsante"
          />
        </FormItem>
        <FormItem name="layout.buttonAlign" label="Allineamento pulsante">
          <AlignmentSelectInput
            name="layout.buttonAlign"
            defaultValue={ButtonAlign.End}
            placeholder="Seleziona l'allineamento"
          />
        </FormItem>
      </RowElement>
      <FormItem
        name="filePath"
        help="Carica il file da poter scaricare in fase di compilazione"
      >
        <UploadInput
          name="filePath"
          buttonProps={{ size: 'large' }}
          customRequest={handleCustomRequest}
          fileList={fileList}
          onRemove={handleRemove}
        />
      </FormItem>
      <FormItem name="label" label="Testo pulsante download">
        <RichTextAdvancedInput
          name="label"
          rows={2}
          menus={MenuPresets.Minimal}
        />
      </FormItem>
    </div>
  );
}
